import { useEffect, useMemo, useState } from "react";
import { ARManager } from "hello-mono-sdk/packages/ar-sdk";
import { ArModes } from "hello-mono-sdk/packages/ar-sdk/build/types/lib/types";
import "./ArButton.scss";
import ArOverlay from "../ArOverlay";
import { HelloProduct } from "../../types/backend";
import arLogo from "../../assets/images/only_ar_logo.png";
import closeIcon from "../../assets/images/close-black.png";
interface Settings {
  primaryColor: string;
  buttonText: string;
  buttonPlacement: "top" | "bottom" | "none";
  showLogo: boolean;
  expectedArModes: ArModes[];
}

const BOTTOM_PLACEMENT = {
  bottom: 10,
  right: 10,
};

const TOP_PLACEMENT = {
  top: 10,
  right: 10,
};

const findSettings = (product: HelloProduct): Settings => {
  type AllowedPlacement = "top" | "bottom" | "none";
  let primaryColor = "#57ca85";
  let buttonText = "View In Your Own Space";
  let buttonPlacement: AllowedPlacement = "bottom";
  let showLogo = true;
  let expectedArModes: ArModes[] = ["webxr", "scene-viewer", "quick-look"];
  const application = product.application;

  const allowedPlacementValues: AllowedPlacement[] = ["top", "bottom", "none"];
  if (typeof application !== "string") {
    const sdkSettings = application.sdk_settings;
    if (sdkSettings) {
      const primaryColorBackend = sdkSettings.primary_colour;
      if (primaryColor && primaryColor.length > 0) {
        primaryColor = primaryColorBackend;
      }

      const buttonTextBackend = sdkSettings.ar_button_text;
      if (buttonTextBackend && buttonTextBackend.length > 0) {
        buttonText = buttonTextBackend;
      }

      const buttonPlacementBackend = sdkSettings.ar_button_position;
      if (buttonPlacementBackend && buttonPlacementBackend.length > 0) {
        if (allowedPlacementValues.includes(buttonPlacementBackend)) {
          buttonPlacement = buttonPlacementBackend;
        }
      }

      const showLogoBackend = sdkSettings.show_helloar_logo;
      if (typeof showLogoBackend === "boolean") {
        showLogo = showLogoBackend;
      }

      const arModesBackend = sdkSettings.model_viewer_settings?.ar_modes;
      if (arModesBackend && arModesBackend.length > 0) {
        const webxrIndex = arModesBackend.indexOf("webxr");
        const sceneViewerIndex = arModesBackend.indexOf("scene-viewer");
        const quickLookIndex = arModesBackend.indexOf("quick-look");
        let arr: { mode: ArModes; priority: number }[] = [
          { mode: "webxr", priority: webxrIndex },
          { mode: "scene-viewer", priority: sceneViewerIndex },
          { mode: "quick-look", priority: quickLookIndex },
        ];

        arr = arr.filter((item) => item.priority !== -1);
        arr.sort((a, b) => {
          if (a.priority < b.priority) {
            return -1;
          } else if (a.priority > b.priority) {
            return 1;
          }
          return 0;
        });

        expectedArModes = arr.map((item) => item.mode);
      }
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const queryPrimaryColor = urlParams.get("primary_color");
  if (queryPrimaryColor && queryPrimaryColor.length > 0) {
    primaryColor = queryPrimaryColor;
  }

  const queryButtonPlacement = urlParams.get("ar_button_position");
  if (queryButtonPlacement && queryButtonPlacement.length > 0) {
    allowedPlacementValues.some((placement) => {
      if (placement === queryButtonPlacement) {
        buttonPlacement = queryButtonPlacement;
        return true;
      }
      return false;
    });
  }

  const queryDisableAr = urlParams.get("should_disable_ar");
  if (queryDisableAr && queryDisableAr.length > 0) {
    if (queryDisableAr === "true") {
      buttonPlacement = "none";
    }
  }

  return {
    primaryColor: primaryColor,
    buttonText: buttonText,
    buttonPlacement: buttonPlacement,
    showLogo: showLogo,
    expectedArModes: expectedArModes,
  };
};

const manager = new ARManager({
  arModes: ["webxr", "scene-viewer", "quick-look"],
});

interface IProps {
  product: HelloProduct;
  margin?: string;
  beforeArStart: () => void;
  onArEnded: () => void;
}

const ArButton: React.FC<IProps> = ({
  product,
  margin,
  beforeArStart,
  onArEnded,
}) => {
  const [supportedModes, setSupportedModes] = useState<ArModes[]>([]);
  const settings = useMemo(() => {
    return findSettings(product);
  }, [JSON.stringify(product)]);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    manager.getSupportedArModes().then((modes) => {
      if (modes) {
        setSupportedModes(modes);
      }
    });
  }, []);

  useEffect(() => {
    manager.updateExpectedModes(settings.expectedArModes);
  }, [settings.expectedArModes]);

  const handleClick = async () => {
    if (supportedModes.length === 0) {
      setShowQR(true);
      return;
    }
    const arDiv = document.createElement("div");
    if (!arDiv) {
      console.error("Can't start AR: Div Not Provided");
      return;
    }
    const placement = product.three_dee_model?.ar_placement;

    const emitter = manager.getEmitter();

    const ReactDOM = await import("react-dom");
    ReactDOM.render(
      <ArOverlay
      tiltX = {manager.tiltXAxis}
      tiltY = {manager.tiltYAxis}
      tiltZ = {manager.tiltZAxis}
      translateX = {manager.translateXAxis}
      translateY = {manager.translateYAxis}
      translateZ = {manager.translateZAxis}
      showControls = {manager.showControls}
      primary_color = {settings.primaryColor}
      emitter={emitter}
        retryAR={() => {
          manager.restartDetection(true);
        }}
        resetAR={() => {
          manager.reset();
        }}
        placement={placement === "ceiling" ? "ceiling" :placement === "wall" ? "wall" : "floor"}
      />,
      arDiv
    );

    beforeArStart();

    const envMapLegacy = product?.rendering_rules?.envMapMeta;
    const envMapV2 = product?.rendering_rules?.envMapV2;
    const envMap = envMapV2 || envMapLegacy;

    let envMapUrl: string | undefined = undefined;
    if (envMap) {
      envMapUrl = envMap.map;
    }

    if (!envMapUrl || envMapUrl.length === 0) {
      envMapUrl = "https://content.helloviewer.io/maps/png/photo_studio.jpg";
    }

    manager.startAr({
      modelDetails: {
        glb: product.three_dee_model?.glb,
        usdz: product.three_dee_model?.usdz,
      },
      settings: {
        wallPlacement: placement === "wall",
        ceilingPlacement: placement === "ceiling",
        domOverlay: arDiv,
        envMap: envMapUrl,
        showSize: true,
      },
      onArEnded: () => {
        ReactDOM.unmountComponentAtNode(arDiv);
        onArEnded();
      },
    });
  };

  return (
    <div >
      {showQR && (
        <div className="no-ar-container">
          <div className="no-ar-message-container">
            Your device doesn't support this feature. Supported devices are:
            iPhone 6s & Above. iPad Pro 2017 & above and ARCore supported
            android devices
            <div className="qr-image-container">
              <img src={product.qr_code_image} />
              <p>Scan this QR code with an AR supported device</p>
            </div>
            <div className="close-icon-container">
              <button
                onClick={() => {
                  setShowQR(false);
                }}
              >
                <img src={closeIcon} />
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="ar-button-container"
        style={{
          display: settings.buttonPlacement === "none" ? "none" : "block",
          ...(settings.buttonPlacement === "top"
            ? TOP_PLACEMENT
            : BOTTOM_PLACEMENT),
          margin: margin ? margin : "",
        }}
      >
        <button className="ar-button helloar-cta-button" onClick={handleClick}>
          {settings.showLogo && <img src={arLogo} />} {settings.buttonText}
        </button>
      </div>
    </div>
  );
};

export default ArButton;
